import { useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

export default function DashboardFilters({
  filterType,
  setFilterType,
  year,
  setYear,
  dateRange,
  setDateRange,
}) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 3 }, (_, i) => currentYear - i);
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const fromYear = dateRange.from
    ? new Date(dateRange.from).getFullYear()
    : null;

  const toYear = dateRange.to
    ? new Date(dateRange.to).getFullYear()
    : null;

  useEffect(() => {
    if (fromYear && toYear && fromYear !== toYear) {
      setDateRange({ from: dateRange.from, to: null });
    }
  }, [fromYear, toYear, dateRange.from, setDateRange]);

  useEffect(() => {
    if (fromYear && toYear && fromYear === toYear) {
      setYear(fromYear);
    }
  }, [fromYear, toYear, setYear]);

  useEffect(() => {
    if (filterType === "YEAR") {
      setDateRange({ from: null, to: null });
      setYear(currentYear);
    }
  }, [filterType, setDateRange, setYear, currentYear]);

  return (
    <Box
      sx={{
        px: { xs: 2, md: 3 },
        py: 1.5,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: { xs: "center", sm: "flex-start" },
        }}
      >
        {["YEAR", "DATE_RANGE"].map((type) => {
          const active = filterType === type;

          return (
            <Box
              key={type}
              onClick={() => setFilterType(type)}
              sx={{
                px: 2.5,
                py: 0.75,
                fontSize: 12,
                borderRadius: "999px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                border: "1px solid",
                borderColor: active ? "secondary.main" : "divider",
                color: active ? "secondary.main" : "text.primary",
                bgcolor: active ? "action.selected" : "background.paper",
                boxShadow: active ? 2 : 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "secondary.main",
                  bgcolor: "action.hover",
                },
              }}
            >
              {type === "YEAR" ? "Year" : "Date Range"}
            </Box>
          );
        })}
      </Box>

      {filterType === "YEAR" && (
        <Select
          size="small"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          sx={{
            width: { xs: "100%", sm: 100 },
            "& .MuiSelect-select": {
              lineHeight: "normal",
            },
          }}
        >
          {years.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </Select>
      )}

      {filterType === "DATE_RANGE" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <TextField
              type="date"
              size="small"
              inputRef={fromRef}
              value={dateRange.from || ""}
              onChange={(e) =>
                setDateRange({ from: e.target.value, to: null })
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => fromRef.current?.showPicker()}
                    >
                      <CalendarTodayIcon
                        sx={{ fontSize: 14, color: "text.secondary" }}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 150 },
                "& input::-webkit-calendar-picker-indicator": {
                  display: "none",
                },
              }}
            />

            <TextField
              type="date"
              size="small"
              inputRef={toRef}
              value={dateRange.to || ""}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, to: e.target.value }))
              }
              inputProps={{
                min: dateRange.from || undefined,
                max: dateRange.from
                  ? `${new Date(dateRange.from).getFullYear()}-12-31`
                  : undefined,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => toRef.current?.showPicker()}
                    >
                      <CalendarTodayIcon
                        sx={{ fontSize: 14, color: "text.secondary" }}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 150 },
                "& input::-webkit-calendar-picker-indicator": {
                  display: "none",
                },
              }}
            />
          </Box>

          <Typography
            component="span"
            sx={{ fontSize: "11px", color: "text.secondary" }}
          >
            Date range must be within the same year
          </Typography>
        </Box>
      )}
    </Box>
  );
}